import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Zap } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function TrialBanner() {
  const { user } = useAuth();
  if (!user || !user.subscription) return null;
  const sub = user.subscription;
  // Active subscription — hide
  if (sub.status === "active") return null;

  if (sub.status === "trialing" && !sub.trial_expired) {
    if (sub.trial_days_left > 7) return null; // Only nudge in the final week
    return (
      <div className="mb-4 pc-card border border-blue-500/40 bg-blue-500/5 p-3 flex items-center gap-3 text-sm" data-testid="trial-banner">
        <Zap className="text-blue-300" size={16}/>
        <div className="flex-1 text-slate-200">
          <strong>{sub.trial_days_left} day{sub.trial_days_left === 1 ? "" : "s"} left in your free trial.</strong>{" "}
          <span className="text-slate-400">After that you'll keep read access, but baseline, templates, invites, exports and AI summaries will pause until you upgrade.</span>
        </div>
        <Link to="/pricing" data-testid="trial-upgrade" className="shrink-0 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
          Upgrade — $29/mo
        </Link>
      </div>
    );
  }

  // Trial expired or inactive → hard banner
  return (
    <div className="mb-4 pc-card border border-rose-500/50 bg-rose-500/5 p-3 flex items-center gap-3 text-sm" data-testid="paywall-banner">
      <AlertTriangle className="text-rose-300" size={16}/>
      <div className="flex-1 text-slate-200">
        <strong>Your trial has ended.</strong>{" "}
        <span className="text-slate-400">You can still read every project, but advanced actions (baseline, templates, invites, share, exports, weekly reports, AI summaries) are paused.</span>
      </div>
      <Link to="/pricing" data-testid="paywall-upgrade" className="shrink-0 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
        Upgrade to continue
      </Link>
    </div>
  );
}

/** Hook: returns {active, locked} where locked is true if advanced features should be disabled */
export function useSubscription() {
  const { user } = useAuth();
  const sub = user?.subscription;
  const active = sub?.active ?? true;
  return { active, locked: !active, state: sub };
}
